import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { loginUser } from "../loginSlice";
import "./login.css";
import "../landing/landing.css";
import { setBackButtonUrl } from "../uiSlice";
import { selectAdminToken } from "../../admin/sessionSlice";
import { s3PublicUrl } from "../../utils/s3PublicUrl";
import { autoJoinHunt } from "../../functions/autoJoinHunt";
import { sendReport, extractReportIdFromResponse } from "../../functions/sendReport";
import Swal from "sweetalert2";

const RESUME_WAND_PLACEHOLDER = "__hp_resume__";

function Login() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const adminToken = useSelector(selectAdminToken);
  const { status, user, error } = useSelector((state) => state.auth);
  const { data } = useSelector((state) => state.theme);
  const [reportChecking, setReportChecking] = useState(false);
  const [joining, setJoining] = useState(false);
  const resumeRouteDoneRef = useRef(false);
  const sentToConfigureRef = useRef(false);
  const fromMobileAppFromQuery = (() => {
    const raw = new URLSearchParams(window.location.search).get("fromMobileApp");
    const normalized = String(raw || "").trim().toLowerCase();
    return normalized === "true" || normalized === "1" || normalized === "yes";
  })();

  // Dynamic branding from the admin/superadmin theme — falls back to sensible
  // defaults until fetchThemeData (triggered from loginUser) resolves.
  const huntTitle = data?.title || data?.huntName || "Treasure Hunt";
  const subHeading = data?.subHeading || "";
  const rawLogo = data?.hunt_client_logo || data?.logo;
  const clientLogo = rawLogo ? s3PublicUrl(rawLogo) : "";
  const rawCover = data?.background_mob || data?.hunt_cover_image || data?.background_desk;
  const coverImage = rawCover ? s3PublicUrl(rawCover) : "";
  const headingColor = data?.headingColor || "#D83307";
  const textColor = data?.textColor || "#5B5B5B";
  const buttonBg = data?.buttonBgColor || "#D83307";
  const buttonText = data?.buttonTextColor || "#FFFFFF";

  // Apply the dynamic hunt background to <body> (not a wrapper div) so it always
  // fills the full page with no extra scroll, and restore on unmount.
  useEffect(() => {
    document.body.classList.add("login-static-bg");
    if (coverImage) {
      document.body.style.backgroundImage = `url("${encodeURI(coverImage)}")`;
    }
    return () => {
      document.body.classList.remove("login-static-bg");
      document.body.style.backgroundImage = "";
    };
  }, [coverImage]);

  useEffect(() => {
    if (fromMobileAppFromQuery) {
      sessionStorage.setItem("fromMobileApp", "true");
    } else {
      sessionStorage.removeItem("fromMobileApp");
    }
  }, [fromMobileAppFromQuery]);

  useEffect(() => {
    dispatch(loginUser());
  }, [dispatch]);

  useEffect(() => {
    if (status === "succeeded" && user?.backButtonRedirect) {
      dispatch(setBackButtonUrl(user.backButtonRedirect));
    }
  }, [status, user, dispatch]);

  useEffect(() => {
    if (status !== "failed") return;
    const msg = error ? String(error) : "Session expired";
    const redirect =
      user && user.backButtonRedirect ? user.backButtonRedirect : "/";
    Swal.fire("Session Expired!", msg, "error").then(() => {
      window.location.href = redirect;
    });
  }, [status, error, user]);

  // A link like "/?otp=...&admin=true" is how an admin's OTP resolves to their session — once
  // login succeeds, send them straight to the admin dashboard instead of the player flow below.
  const isAdminLink = useMemo(() => {
    const params = new URLSearchParams(window.location.search);
    return String(params.get("admin") || "").trim().toLowerCase() === "true";
  }, []);

  useEffect(() => {
    if (!isAdminLink || status !== "succeeded" || !user) return;
    // loginUser() already dispatched setSession() with this sessionId/organizationId, which
    // persists the admin token to localStorage (see sessionSlice.js) — a plain "/admin" picks
    // that session back up on load, no need to carry it in the URL too.
    window.location.href = "/admin";
  }, [isAdminLink, status, user]);

  useEffect(() => {
    if (isAdminLink) return;
    if (status !== "succeeded" || !user) return;

    const isDemo =
      user.source === "DEMO" ||
      String(user.role || user.user_type || "").toLowerCase() === "demobypass";
    // Demo flow doesn't have admin token / report resume. Stay on login and wait for user to click "Begin Play".
    if (isDemo) return;

    if (user?.guessMePlayed === true && !resumeRouteDoneRef.current) {
      resumeRouteDoneRef.current = true;
      const back = user.backButtonRedirect || "/";
      Swal.fire("Already played", " ", "error").then(() => {
        window.location.href = back;
      });
      return;
    }

    const uid = user.userId || user.userid || user.id;
    const sid = user.sessionId;
    const oid = user.organizationId;
    if (!uid || !sid || !oid || !adminToken) return;
    if (resumeRouteDoneRef.current) return;

    const backendBase = String(process.env.REACT_APP_BACKEND_URL || "").replace(/\/+$/, "");
    if (!backendBase) return;

    let cancelled = false;
    // Check existing report in background; do not block the login screen.
    setReportChecking(true);

    (async () => {
      try {
        const res = await fetch(`${backendBase}/fetchReport`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${adminToken}`,
          },
          body: JSON.stringify({
            userId: String(uid),
            sessionId: String(sid),
            organizationId: String(oid),
          }),
        });
        const reportData = await res.json().catch(() => ({}));
        if (cancelled || resumeRouteDoneRef.current) return;

        const back = user.backButtonRedirect || "/";
        const rpt = reportData.success ? reportData.report : null;

        if (rpt && Number(rpt.gameover) === 1) {
          resumeRouteDoneRef.current = true;
          await Swal.fire("You have already played!", " ", "error");
          window.location.href = back;
          return;
        }

        if (rpt && Number(rpt.gameover) === 1) {
          resumeRouteDoneRef.current = true;
          const s1 = rpt.stage1 != null ? Number(rpt.stage1) : 0;
          navigate("/stage2/broom", {
            replace: true,
            state: {
              wandId: RESUME_WAND_PLACEHOLDER,
              points: Number.isFinite(s1) ? s1 : 0,
              time: rpt.time != null ? String(rpt.time) : "",
              reportId: rpt.reportId,
              stageId: rpt.id != null ? rpt.id : undefined,
            },
          });
          return;
        }

        if (rpt && Number(rpt.stage2) > 0 && Number(rpt.gameover) !== 2) {
          resumeRouteDoneRef.current = true;
          const s1 = rpt.stage1 != null ? Number(rpt.stage1) : NaN;
          const s2 = rpt.stage2 != null ? Number(rpt.stage2) : NaN;
          const pts = Number(rpt.points) || (Number.isFinite(s1) && Number.isFinite(s2) ? s1 + s2 : 0);
          navigate("/thankyou", {
            replace: true,
            state: {
              points: pts,
              time: rpt.time != null ? String(rpt.time) : "",
              stageId: rpt.id != null ? rpt.id : undefined,
              spellScore: Number.isFinite(s1) ? s1 : pts - (Number.isFinite(s2) ? s2 : 0),
              flightScore: Number.isFinite(s2) ? s2 : 0,
            },
          });
          return;
        }

        // No resume route; stay on login and wait for the user to click "Begin Play".

      } catch (e) {
        console.error("login fetchReport:", e);
      } finally {
        if (!cancelled) setReportChecking(false);
      }
    })();

    return () => {
      cancelled = true;
      setReportChecking(false);
    };
  }, [status, user, adminToken, navigate]);

  const handlePlay = async () => {
    if (joining) return;
    // loginSlice.js already resolved and joined this hunt during login itself, from a lobbyPin
    // that happened to survive (see buildJoinLink) — nothing left to do here but go straight to
    // Team Registration with what it already saved.
    if (user?.stageId) {
      navigate("/team-registration", { state: { stageId: user.stageId, themeName: user.joinedThemeName } });
      return;
    }
    // The OTP gateway resolves sessionId/organizationId from its own login response every time —
    // never from a URL param surviving its redirect, which turned out not to be reliable at all
    // (a lobbyPin never survives it, and neither would a "came via join link" flag). Since a
    // resolved session always identifies exactly one active hunt, always auto-join directly
    // rather than gating on any signal that the URL supposedly carries a join link.
    setJoining(true);
    try {
      const uid = user?.userId || user?.userid || user?.id;

      // Seed an external report row the moment this player joins the hunt (guest/anonymous
      // player, matching the same GUEST_USER convention used elsewhere in this app). Failure
      // here must never block the actual join — the report is telemetry, not gameplay state.
      let reportId = user?.reportId;
      if (!reportId) {
        try {
          const seeded = await sendReport({
            sessionId: user?.sessionId,
            organizationId: user?.organizationId,
            role: "GUEST_USER",
            token: "",
            name: user?.name || "",
            userId: uid,
            gameId: process.env.REACT_APP_EXTRAMILE_GAME_ID || "",
            points: 0,
            time: "00:00",
          });
          reportId = extractReportIdFromResponse(seeded, uid);
        } catch (e) {
          console.warn("[login] sendReport insert failed:", e?.message || e);
        }
      }

      const resp = await autoJoinHunt({
        token: adminToken,
        userId: uid,
        email: user?.email,
        fullName: user?.name,
        employeeId: user?.employeeId,
        reportid: reportId,
        user_type: user?.role || user?.source || "GUEST_USER",
      });
      if (!resp?.success) {
        navigate("/join");
        return;
      }
      try {
        sessionStorage.setItem(
          "gameSettings",
          JSON.stringify({
            stageId: resp.stageId,
            themeName: resp.themeName,
            lobby_pin: resp.lobby_pin,
            lobby_link: resp.lobby_link,
            reportId,
          })
        );
      } catch {
        // ignore
      }
      navigate("/team-registration", { state: { stageId: resp.stageId, themeName: resp.themeName } });
    } catch {
      navigate("/join");
    } finally {
      setJoining(false);
    }
  };

  if (status === "loading" || (isAdminLink && status === "succeeded")) {
    return (
      <div className="login-main-container">
        <div className="quiz-loader-container">
          <div className="quiz-loader"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="landing-page">
      <div className="landing-desktop-gate">
        <div className="landing-desktop-gate-card">
          <i className="fa-solid fa-mobile-screen-button landing-desktop-gate-icon" />
          <h2>This game is played on mobile</h2>
          <p>Open this link on your phone to play. Resize this window or switch devices to continue.</p>
        </div>
      </div>

      {!coverImage && (
        <>
          <div className="landing-blob landing-blob--peach" />
          <div className="landing-blob landing-blob--mint" />
        </>
      )}

      <div className="landing-content">
        {clientLogo && (
          <img src={clientLogo} alt="Client logo" className="landing-client-logo-standalone" />
        )}

        <h1 className="landing-hunt-title" style={{ color: headingColor }}>
          {huntTitle}
        </h1>
        {subHeading && (
          <p className="landing-hunt-sub" style={{ color: textColor }}>
            {subHeading}
          </p>
        )}

        {user && status === "succeeded" && (
          <>
            {user?.guessMePlayed ? (
              <button
                type="button"
                className="landing-play-btn"
                aria-label="Already played"
                onClick={() => {
                  const back = user.backButtonRedirect || "/";
                  Swal.fire("Already played", " ", "error").then(() => {
                    window.location.href = back;
                  });
                }}
                style={{ opacity: 0.7, cursor: "not-allowed" }}
              >
                ALREADY PLAYED
              </button>
            ) : (
              <button
                type="button"
                className="landing-play-btn"
                style={{ background: buttonBg, color: buttonText }}
                aria-label="Begin play"
                onClick={handlePlay}
                disabled={joining}
              >
                {joining ? "JOINING..." : "PLAY"}
              </button>
            )}
          </>
        )}

        <div className="landing-footer">Powered by ExtraMile Play</div>
      </div>
    </div>
  );
}

export default Login;
